import { API_CONFIG } from '../../config/api';
import type { TreeNodeData } from '../../design/TreeSelector';

// Uses the same GroupFolder backend endpoints but provides enterprise-specific helpers

const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
});

export interface FolderItem {
    id: string;
    _id?: string;
    name: string;
    description: string;
    parentFolderId?: string | null;
    createdBy: {
        id: string;
        firstName?: string;
        lastName?: string;
        preferredName?: string;
        email: string;
    };
    createdAt: string;
    updatedAt: string;
}

/**
 * Fetch root-level folders for the current enterprise user
 */
export const fetchRootFolders = async (): Promise<FolderItem[]> => {
    const res = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/root`, {
        headers: getAuthHeaders(),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Failed to fetch root folders');
    return json.data.folders.map((f: any) => ({ ...f, id: f._id || f.id }));
};

/**
 * Fetch the contents (child folders + child groups) of a specific folder
 */
export const fetchFolderContents = async (folderId: string): Promise<{
    folder: FolderItem;
    childFolders: FolderItem[];
    childGroups: any[];
    breadcrumb: { id: string; name: string }[];
}> => {
    const res = await fetch(`${API_CONFIG.ENDPOINTS.GROUP_FOLDERS}/${folderId}/contents`, {
        headers: getAuthHeaders(),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Failed to fetch folder contents');

    return {
        folder: { ...json.data.folder, id: json.data.folder._id || json.data.folder.id },
        childFolders: json.data.childFolders.map((f: any) => ({ ...f, id: f._id || f.id })),
        childGroups: json.data.childGroups || [],
        breadcrumb: json.data.breadcrumb || [],
    };
};

/**
 * Recursively build the TreeNodeData[] structure by fetching all folders.
 * Starts from root folders, then for each folder that has children, fetches their contents.
 */
export const buildTreeFromApi = async (): Promise<TreeNodeData[]> => {
    const rootFolders = await fetchRootFolders();
    return Promise.all(rootFolders.map(folder => buildNodeRecursively(folder)));
};

const buildNodeRecursively = async (folder: FolderItem): Promise<TreeNodeData> => {
    try {
        const contents = await fetchFolderContents(folder.id);
        const childNodes = await Promise.all(
            contents.childFolders.map(child => buildNodeRecursively(child))
        );

        return {
            id: folder.id,
            label: folder.name,
            children: childNodes.length > 0 ? childNodes : undefined,
        };
    } catch {
        // If fetching children fails, return the folder as a leaf node
        return {
            id: folder.id,
            label: folder.name,
        };
    }
};

/**
 * Create a new folder in the hierarchy
 */
export const createEnterpriseFolder = async (data: {
    name: string;
    description: string;
    parentFolderId?: string;
}): Promise<FolderItem> => {
    const res = await fetch(API_CONFIG.ENDPOINTS.GROUP_FOLDERS, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Failed to create folder');
    return { ...json.data, id: json.data._id || json.data.id };
};

/**
 * Fetch all enterprise groups
 */
export const fetchEnterpriseGroups = async (): Promise<any[]> => {
    const res = await fetch(`${API_CONFIG.ENDPOINTS.GROUPS}`, {
        headers: getAuthHeaders(),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Failed to fetch groups');
    return json.data?.groups || [];
};
